import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Field, change } from "redux-form";
import { decodeJwt } from 'jose';

import ApplicationLayout, { applicationStyles as styles } from "../ApplicationLayout";
import WithUserForm from "../../Register/WithUserForm";
import { Turnstile } from "@marsidev/react-turnstile";

// Matches the code length configured on the Twilio Verify service.
const VERIFY_CODE_LENGTH = 4;
const RESEND_COOLDOWN_SECONDS = 30;

const digitsOnly = (value, previousValue) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length > VERIFY_CODE_LENGTH ? previousValue : digits;
};

const CodeField = ({ input, id, invalid, disabled, autoFocus, displayValue }) => (
  <div className={`${styles.box} ${styles.codeBox} ${invalid ? styles.boxInvalid : ""} ${disabled ? styles.boxDisabled : ""}`}>
    <input
      {...input}
      value={displayValue || input.value}
      id={id}
      className={styles.codeInput}
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={VERIFY_CODE_LENGTH}
      placeholder="····"
      disabled={disabled}
      autoFocus={autoFocus}
    />
  </div>
);

const useResend = (send) => {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (remaining <= 0) return undefined;
    const timer = setTimeout(() => setRemaining(remaining - 1), 1000);
    return () => clearTimeout(timer);
  }, [remaining]);

  return {
    sent: remaining > 0,
    resend: async () => {
      if (remaining > 0) return;
      setRemaining(RESEND_COOLDOWN_SECONDS);
      await send();
    },
  };
};

const Tick = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="m3 8.5 3.5 3.5L13 5" />
  </svg>
);

const CodeSection = ({ id, name, label, sentTo, resend, error, verified, verifiedCode, autoFocus, children }) => (
  <div className={styles.field}>
    <label className={styles.fieldLabel} htmlFor={id}>{label}</label>
    <div className={styles.fieldHint}>Sent to {sentTo}</div>
    <Field
      name={name}
      id={id}
      component={CodeField}
      normalize={digitsOnly}
      invalid={!!error}
      displayValue={verified ? verifiedCode : ""}
      disabled={verified}
      autoFocus={autoFocus}
    />
    {error && <div className={styles.error}>{error}</div>}
    {verified ? (
      <div className={styles.verified} role="status">
        <Tick />
        Verified
      </div>
    ) : (
      <button
        type="button"
        className={styles.link}
        onClick={resend.resend}
        disabled={resend.sent}
        style={{ alignSelf: "flex-start", minHeight: 32 }}
      >
        {resend.sent ? "New code sent" : "Send a new code"}
      </button>
    )}
    {children}
  </div>
);

const Form = ({ webLoginToken, email, phoneNumber, form, machine, sendPhoneNumberVerifyCode, sendEmailVerifyCode, getTurnstileToken, turnstileError, setTurnstileError }) => {
  const bothVerified = form.isVerifyCode && form.isVerifyEmailCode;

  const goBack = () => {
    if (webLoginToken) {
      window.location.href = `${window.location.origin}/login`;
    } else {
      machine.service.send("BACK");
    }
  };

  const resendOne = (mutate, field) => async () => {
    form.change(field, "");
    form.resetCodesErrors();
    setTurnstileError(false);
    try {
      await mutate({ variables: { webLoginToken, turnstileToken: await getTurnstileToken() } });
    } catch (error) {
      setTurnstileError(true);
    }
  };

  const smsResend = useResend(resendOne(sendPhoneNumberVerifyCode, "code"));
  const emailResend = useResend(resendOne(sendEmailVerifyCode, "emailCode"));

  useEffect(() => {
    if (bothVerified) {
      if (webLoginToken) {
        // If the user came from web login and successfully verified both phone and email,
        // store the user token in localStorage. This token will be used to fetch the currentUser.
        window.localStorage.setItem("token", webLoginToken);
      }
      const timer = setTimeout(() => machine.service.send("NEXT"), 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [bothVerified]);

  const footer = (
    <>
      {bothVerified && <div role="status" className={styles.footerStatus}>Both codes verified.</div>}
      <div className={styles.actions}>
        <button type="submit" className={styles.cta} disabled={!bothVerified}>Continue</button>
        <p className={styles.footerNote}>
          Having trouble? <a href="https://wa.me/443330166335">Contact us</a>
        </p>
      </div>
    </>
  );

  return (
    <ApplicationLayout
      as="form"
      onSubmit={form.handleSubmit}
      step={webLoginToken ? undefined : 2}
      onBack={goBack}
      title="Confirm it's you"
      intro="We've sent a 4-digit code by text and another by email. They can take a minute to arrive."
      footer={footer}
    >
      {turnstileError && (
        <div className={styles.error}>We couldn't verify you're human. Please try again.</div>
      )}

      <CodeSection
        id="smsCode"
        name="code"
        label="Code from text message"
        sentTo={phoneNumber}
        resend={smsResend}
        error={form.verifyCodeError && !form.isVerifyCode ? "That code doesn't match." : ""}
        verified={form.isVerifyCode}
        verifiedCode={form.verifiedPhoneCode}
        autoFocus
      />

      <CodeSection
        id="emailCode"
        name="emailCode"
        label="Code from email"
        sentTo={email}
        resend={emailResend}
        error={form.verifyEmailCodeError && !form.isVerifyEmailCode ? "That code doesn't match." : ""}
        verified={form.isVerifyEmailCode}
        verifiedCode={form.verifiedEmailCode}
      >
        <div className={styles.fieldHint}>Not there? Check your spam or promotions folder.</div>
      </CodeSection>
    </ApplicationLayout>
  );
};

class StepConfirmation extends React.Component {
  constructor(props) {
    super(props);
    this.asyncValidate = this.asyncValidate.bind(this);
    const params = new URLSearchParams(window.location.search);

    this.state = {
      codeSent: false,
      isVerifyCode: false,
      isVerifyEmailCode: false,
      verifiedPhoneCode: "",
      verifiedEmailCode: "",
      verifyCodeError: false,
      verifyEmailCodeError: false,
      turnstileError: false,
      webLoginToken: params.get("session")
    };
  }

  // The form outlives this page, so a member coming back has to enter both codes again.
  componentDidMount() {
    this.props.dispatch(change("apply", "code", ""));
    this.props.dispatch(change("apply", "emailCode", ""));
  }

  TURNSTILE_ENABLED = process.env.REACT_APP_TURNSTILE_ENABLED === 'true';
  turnstileRef = React.createRef();
  setTurnstileError = (turnstileError) => this.setState({ turnstileError });

  // Turnstile tokens are single-use, so reset the widget to mint a fresh one before each action.
  getTurnstileToken = async () => {
    if (!this.TURNSTILE_ENABLED) return null;
    const widget = this.turnstileRef.current;
    widget.reset();
    widget.execute();
    const token = await widget.getResponsePromise();
    widget.reset();
    return token;
  };

  phoneCheckInFlight = false;
  lastPhoneCodeChecked = null;
  emailCheckInFlight = false;
  lastEmailCodeChecked = null;

  resetCodesErrors = () => {
    this.lastPhoneCodeChecked = null;
    this.lastEmailCodeChecked = null;
    this.setState({verifyCodeError: false, verifyEmailCodeError: false});
  };

  async asyncValidate(values, _, props, changedField) {
    const isWebLogin = !!this.state.webLoginToken;

    if (!this.state.isVerifyCode && (!changedField || changedField === "code")) {
      const phoneCodeComplete = values.code && values.code.length === VERIFY_CODE_LENGTH;
      if (phoneCodeComplete && !this.phoneCheckInFlight && values.code !== this.lastPhoneCodeChecked) {
        this.phoneCheckInFlight = true;
        this.lastPhoneCodeChecked = values.code;
        this.setState({ verifyCodeError: false });

        try {
          const mutationFn = isWebLogin
              ? props.checkPhoneNumberVerifyCodeForWebLogin
              : props.checkPhoneNumberVerifyCode;

          const variables = {
            code: values.code,
            turnstileToken: await this.getTurnstileToken(),
            ...(isWebLogin && { webLoginToken: this.state.webLoginToken })
          };

          const mutation = await mutationFn({ variables });

          const result = isWebLogin
              ? mutation.data.checkPhoneNumberVerifyCodeForWebLogin
              : mutation.data.checkPhoneNumberVerifyCode;

          if (result) {
            this.setState({
              isVerifyCode: true,
              verifiedPhoneCode: values.code,
              turnstileError: false,
              webLoginToken: result.token
            });
          } else {
            this.lastPhoneCodeChecked = null;
            this.setState({ verifyCodeError: true });
          }
        } catch {
          this.setState({ turnstileError: true });
        } finally {
          this.phoneCheckInFlight = false;
        }
      }
    }

    if (!this.state.isVerifyEmailCode && (!changedField || changedField === "emailCode")) {
      const emailCodeComplete = values.emailCode && values.emailCode.length === VERIFY_CODE_LENGTH;
      if (emailCodeComplete && !this.emailCheckInFlight && values.emailCode !== this.lastEmailCodeChecked) {
        this.emailCheckInFlight = true;
        this.lastEmailCodeChecked = values.emailCode;
        this.setState({ verifyEmailCodeError: false });
        try {
          const mutationFn = isWebLogin
              ? props.checkEmailVerifyCodeForWebLogin
              : props.checkEmailVerifyCode;

          const variables = {
            code: values.emailCode,
            turnstileToken: await this.getTurnstileToken(),
            ...(isWebLogin && { webLoginToken: this.state.webLoginToken })
          };

          const mutation = await mutationFn({ variables });

          const result = isWebLogin
              ? mutation.data.checkEmailVerifyCodeForWebLogin
              : mutation.data.checkEmailVerifyCode;

          if (result) {
            this.setState({
              isVerifyEmailCode: true,
              verifiedEmailCode: values.emailCode,
              turnstileError: false,
              webLoginToken: result.token
            });
          } else {
            this.lastEmailCodeChecked = null;
            this.setState({ verifyEmailCodeError: true });
          }
        } catch {
          this.setState({ turnstileError: true });
        } finally {
          this.emailCheckInFlight = false;
        }
      }
    }
  }

  render() {
    return (
        <>
        {this.TURNSTILE_ENABLED && (
            <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1000 }}>
                <Turnstile
                    ref={this.turnstileRef}
                    siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                    options={{ appearance: 'interaction-only', execution: 'execute' }}
                />
            </div>
        )}
        <WithUserForm
            formRequiresAuthentication={!this.state.webLoginToken}
            asyncValidate={this.asyncValidate}
            asyncChangeFields={["code", "emailCode"]}
            isVerifyCode={this.state.isVerifyCode}
            isVerifyEmailCode={this.state.isVerifyEmailCode}
            verifiedPhoneCode={this.state.verifiedPhoneCode}
            verifiedEmailCode={this.state.verifiedEmailCode}
            verifyCodeError={this.state.verifyCodeError}
            verifyEmailCodeError={this.state.verifyEmailCodeError}
            resetCodesErrors={this.resetCodesErrors}
            render={(props) => {
              const userData = props.userQuery?.data?.currentUser;
              let userEmail = userData?.email;
              let userPhone = userData?.phoneNumber;

              if(!!this.state.webLoginToken) {
                const payload = decodeJwt(this.state.webLoginToken);
                userEmail = payload.email
                userPhone = payload.phoneNumber
              }
              return (<Form webLoginToken={this.state.webLoginToken} email={userEmail} phoneNumber={userPhone} getTurnstileToken={this.getTurnstileToken} turnstileError={this.state.turnstileError} setTurnstileError={this.setTurnstileError} {...props} />)
            }}
        />
        </>
    );
  }
}

export default connect()(StepConfirmation);
